import PageContainer from "@/pages/containers/page-container";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { escapeRegExp, htmlFromString, normalizeText } from "@/lib/string-util";
import LoadingIndiator from "./components/loading-indiator";
import SearchBar from "./components/shared/search-bar";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "./components/error-notification";
import useUserInfo from "@/hooks/use-user-info";
import { useMyBenefits } from "@/hooks/use-my-benefits";
import type { AxiosError } from "axios";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useUserDataQueries } from "@/hooks/use-login";
import { useFeatureName } from "@/hooks/use-pageTitle";
import type { Benefit } from "@/model/my-benefits";
import type { IUiLabelsResponse } from "@/model/ui-labels";
import { queryClient } from "@/hooks/use-query-client";
import { QueryKeys } from "@/constants";
import { DEFAULT_NETWORK_VALUES } from "@/constants/ui_labels";
import { isRbpClient } from "@/lib/client-utils";

const MyBenefit = () => {
  const featureName = useFeatureName();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const [searchTerm, setSearchTerm] = useState("");
  const userInfo = useUserInfo();
  const myBenefitsQuery = useMyBenefits(userInfo, activeCoverage);
  const networkUiLabels = queryClient.getQueryData<
    IUiLabelsResponse | undefined
  >([QueryKeys.UI_LABELS])?.data?.network;
  const { addError } = useNotificationError();
  const { globalFlagsQuery } = useUserDataQueries({
    groupId: userInfo?.groupId || "",
    divisionId: userInfo?.divisionId || "",
    accessToken: userInfo?.accessToken || "",
    username: userInfo?.username || "",
  });

  const normalizedBenefit = (benefit: Benefit) => {
    return {
      name: normalizeText(benefit.benefitName),
      inNetwork: normalizeText(benefit.benefitDescriptionIn),
      outNetwork: normalizeText(benefit.benefitDescriptionOut),
    };
  };

  const filteredBenefit = useMemo(() => {
    if (!Array.isArray(myBenefitsQuery.data?.benefitByServiceCategory))
      return { inNetwork: [], outNetwork: [] };

    const benefits = myBenefitsQuery.data?.benefitByServiceCategory;

    const inNetwork = benefits.filter(
      (benefit) =>
        normalizedBenefit(benefit)
          .name?.toLowerCase()
          .includes(searchTerm?.toLowerCase()) ||
        normalizedBenefit(benefit)
          .inNetwork?.toLowerCase()
          .includes(searchTerm?.toLowerCase()),
    );

    const outNetwork = benefits.filter(
      (benefit) =>
        normalizedBenefit(benefit)
          .name?.toLowerCase()
          .includes(searchTerm?.toLowerCase()) ||
        normalizedBenefit(benefit)
          .outNetwork?.toLowerCase()
          .includes(searchTerm?.toLowerCase()),
    );

    return { inNetwork, outNetwork };
  }, [myBenefitsQuery.data, searchTerm]);

  const renderBenefitDescription = (
    text: string | undefined,
    highlightMatch: (t: string) => React.ReactNode,
  ) => {
    if (!text) return null;

    const newLines = text.replace(/\\n/g, "\n").split(/(\r?\n)/g);

    return newLines.map((linePart, lineIndex) => {
      if (linePart === "\n" || linePart === "\r\n")
        return <br key={lineIndex} />;

      const parts = linePart.split(/(\$\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+%)/g);

      return (
        <span key={lineIndex}>
          {parts.map((part, partIndex) => {
            if (!part) return null;

            const isDollar = /^\$\d{1,3}(?:,\d{3})*(?:\.\d+)?$/.test(part);
            const isPercent = /^\d+%$/.test(part);

            if (isDollar || isPercent) {
              return (
                <span key={partIndex} className="font-bold">
                  {highlightMatch(part)}
                </span>
              );
            }

            return (
              <React.Fragment key={partIndex}>
                {highlightMatch(part)}
              </React.Fragment>
            );
          })}
        </span>
      );
    });
  };

  useEffect(() => {
    if (myBenefitsQuery.isError && myBenefitsQuery.error) {
      const err = (myBenefitsQuery.error as AxiosError).response?.data as {
        message?: string;
      };
      addError({
        message:
          err?.message || "Failed to load benefits. Please try again later.",
        type: ErrorNotificationType.ERROR,
      });
    }
  }, [myBenefitsQuery.isError, myBenefitsQuery.error, addError]);

  const highlightMatch = useCallback(
    (text: string) => {
      if (!searchTerm) return htmlFromString(text);

      const searchTermWithoutSpecialChar = escapeRegExp(searchTerm);
      const regex = new RegExp(`(${searchTermWithoutSpecialChar})`, "gi");
      const parts = normalizeText(text)?.split(regex);

      return parts.map((part: string, index: number) => {
        const partMatches = new RegExp(
          `^${searchTermWithoutSpecialChar}$`,
          "i",
        ).test(part);

        return partMatches ? (
          <span key={index} className="bg-yellow-300 font-semibold">
            {part}
          </span>
        ) : (
          <span key={index}>{htmlFromString(part)}</span>
        );
      });
    },
    [searchTerm],
  );

  if (myBenefitsQuery.isLoading) return <LoadingIndiator />;

  return (
    <PageContainer
      title={`${featureName} ${(userInfo.planCoverageInfo?.length ?? 0) === 1 ? `(${activeCoverage?.claimCoverageCodeDesc ?? ""})` : ""}`}
    >
      {/* tabs section */}
      <Tabs defaultValue="tab1" className="tabs">
        <div>
          <div
            className={`${globalFlagsQuery?.data?.showNetworkTabs && !isRbpClient(userInfo?.memberId) ? "tabs-list-container" : "flex w-full items-center justify-end border-b bg-transparent"} flex-col-reverse gap-4 sm:flex-row`}
          >
            {globalFlagsQuery?.data?.showNetworkTabs &&
              !isRbpClient(userInfo?.memberId) && (
                <TabsList className="tabs-list" style={{ padding: 0 }}>
                  {Object.values(networkUiLabels || DEFAULT_NETWORK_VALUES).map(
                    (networkItem: string, index) => {
                      const value = index + 1;
                      return (
                        <TabsTrigger
                          key={`tab${value}`}
                          className="tabs-trigger"
                          value={`tab${value}`}
                        >
                          {networkItem}
                        </TabsTrigger>
                      );
                    },
                  )}
                </TabsList>
              )}

            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto md:flex-row">
              <Button
                variant="outline"
                type="button"
                className="mb-1 bg-gray-50 font-semibold"
                disabled={myBenefitsQuery.data?.planDocument ? false : true}
              >
                <a
                  href={myBenefitsQuery.data?.planDocument}
                  target="_blank"
                  className="text-primary no-underline"
                  rel="noopener noreferrer"
                >
                  {myBenefitsQuery.data?.planDocument
                    ? myBenefitsQuery.data?.buttonLabel
                    : "SBC Coming Soon"}
                </a>
              </Button>
            </div>
          </div>
          {myBenefitsQuery.data?.benefitByServiceCategory?.length !== 0 && (
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              ariaLabel="Search benefits"
              className="my-4"
            />
          )}
        </div>
        {
          <>
            <TabsContent value="tab1">
              <div className="flex flex-col gap-4">
                {filteredBenefit.inNetwork.length > 0 ? (
                  filteredBenefit.inNetwork.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex cursor-pointer flex-col justify-between gap-8 rounded-md border border-gray-200 bg-white p-4 hover:bg-primary-extraLight"
                    >
                      <div className="flex w-full gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex items-start justify-between">
                            <p className="font-semibold text-gray-900">
                              {highlightMatch(benefit.benefitName)}
                            </p>
                          </div>

                          <p className="text-sm text-gray-600">
                            {renderBenefitDescription(
                              benefit?.benefitDescriptionIn,
                              highlightMatch,
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex h-[500px] w-full items-center justify-center xl:h-[700px]">
                    {/* <NoDataAvailable /> */}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="tab2">
              <div className="flex flex-col gap-4">
                {filteredBenefit.outNetwork.length > 0 ? (
                  filteredBenefit.outNetwork.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex cursor-pointer flex-col justify-between gap-8 rounded-md border border-gray-200 bg-white p-4 hover:bg-primary-extraLight"
                    >
                      <div className="flex w-full gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex items-start justify-between">
                            <p className="font-semibold text-gray-900">
                              {highlightMatch(benefit.benefitName)}
                            </p>
                          </div>
                          {/* <p className="text-sm text-gray-600">
                      {benefit.benefitDescriptionOut}
                    </p> */}

                          <p className="text-sm text-gray-600">
                            {renderBenefitDescription(
                              benefit?.benefitDescriptionOut,
                              highlightMatch,
                            )}
                          </p>
                        </div>
                      </div>
                      {/* TODO: Don't know how the requirement came here to display here */}
                      {/* <div className="my-2 flex h-auto w-full flex-row items-center justify-center gap-4 md:justify-start">
                    {!!parseInt(benefit.outNetworkIndividualAmount ?? "0") && (
                      <Badge className="flex h-10 w-40 justify-center border-blue-200 bg-blue-100 text-sm text-blue-600">
                        <span className="mx-2">Individual</span>
                        {formatCurrency(benefit.outNetworkIndividualAmount)}
                      </Badge>
                    )}
                    {!!parseInt(benefit.outNetworkFamilyAmount ?? "0") && (
                      <Badge className="flex h-10 w-40 justify-center border-orange-200 bg-orange-100 text-sm text-orange-600">
                        <span className="mx-2">Family</span>
                        {formatCurrency(benefit.outNetworkFamilyAmount)}
                      </Badge>
                    )}
                  </div> */}
                    </div>
                  ))
                ) : (
                  <div className="flex h-[500px] min-h-96 w-full items-center justify-center xl:h-[700px]">
                    {/* <NoDataAvailable /> */}
                  </div>
                )}
              </div>
            </TabsContent>
          </>
        }
      </Tabs>
    </PageContainer>
  );
};

export default MyBenefit;
